/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { getCatalogId, IComponentProps, ICoreContext, Image, getUrlSync, IImageSettings, getAsset } from '@msdyn365-commerce/core';
import { ImageFetchPriority } from '@msdyn365-commerce/core-internal';
import {
    GetCustomerInput, getCustomer, buildWishlistLine, getWishlistByCustomerId,
    OrderTemplate
} from '@msdyn365-commerce-modules/retail-actions';
import { ICartState } from '@msdyn365-commerce/global-state';
import { UncontrolledTooltip } from '@msdyn365-commerce-modules/utilities';
import React from 'react';
import { IReactionDisposer, reaction } from 'mobx';
import { Button, ButtonProps } from 'reactstrap';
import classnames from 'classnames';
import debounce from 'lodash/debounce';
import { toast, toasts } from '../toast/toast';
import { ProductListProduct, IActionsAvailable, IProductListResources, BuyValidationResult } from './product-list-component';
import { AsyncResult, CommerceList, Customer, PeriodicDiscountOfferType, ProductList, ProductPrice, SimpleProduct, UnitOfMeasureConversion } from '@msdyn365-commerce/retail-proxy';
import { addLinesAsync, createCommerceListAsync, createGetByCustomerInput, removeLinesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CommerceListsDataActions.g';
import { getLocale } from './locale';
import {
    generateAddToOrderTemplateDialogResources,
    generateOrderTemplateNameDialogResources,
    generateItemAddedToOrderTemplateDialogResources,
    generateDuplicateItemsWhenAddingToOrderTemplateDialogResources
} from './locale/add-to-order-template-resources';
import AddToOrderTemplateComponent from '../../themes/dlvry-adventureworks/views/components/addtoordertemplate.component';
import PQueue from 'p-queue';
import PriceComponent from '../../themes/dlvry-adventureworks/views/components/price.component';
import { humanizeUnitOfMeasure } from '../unit-of-measure';
import { getConversionText, getKilogramConversion, getLiterPrice, getNonBreakoffUOM, getPcsConversion, getRoundedFactor } from '../../shared-utils/unit-conversion-utils';
import DiscountBadge from '../discount-badge/discount-badge';
import { getTemperatureZoneIcon } from '../../shared-utils/attributes/get-temperature-zone';
import { multiplyPriceBy } from '../../shared-utils/price-utils';
import QuantityInput from '../common/quantity-input';
import { IProductAgreement, IProductError } from '../../shared-utils/types';
import { IProductListByProductId } from '../../actions/get-product-lists-by-product-ids';
import { ensureBaseImageUrl } from '../../shared-utils/image-utilities';
import { DebouncedFunc } from 'lodash';
import UnitConversionDetails from '../UnitConversionDetails';
import { IBrandSchemeEntity } from '../../actions/entities/brand-scheme-entity';
import * as GTM from '../../shared-utils/google-tag-manager/gtm';
import { LocalDropdown } from '../dropdown/dropdown';
import { AlternativeProducts, OnChangeProductFunction } from '../dropdown/alternative-products';
import { IProductRelationTypeForProduct } from '../../actions/get-relation-types-by-ids';
import { IAppSettingsInContext } from '../../settings/app.settings.autogenerated';
import { CartHelper } from '../../shared-utils/cart-helper';
import CartCount from '../cart-count';
import { getHideConversionInCategories } from '../../settings/app.settings.utils';
import { getProductPageUrlWithCategoryPathSync, getShowAlternativeProductsType, isAvailableToBuy, isProductInCategory } from '../../shared-utils/common-utils';
import { LifecycleStatus } from '../../shared-utils/lifecycle-status';
import { ExpandableEllipsis } from '../common/expandable-ellipsis';
import { getAgreementBadgeForProduct } from '../../shared-utils/agreement-product-utils';
import { AgreementMark } from '../icons';

/**
 * Data required to render each order history row.
 */
export interface IProductListRowData {
    cart?: ICartState;
    product: ProductListProduct;
    simpleProduct: SimpleProduct | undefined;
    orderTemplate?: OrderTemplate;
    orderTemplates?: OrderTemplate[];
    customerInformation?: Customer;
    customerAgreements?: IProductAgreement[];
    unitConversions?: UnitOfMeasureConversion[];
    wishlists?: CommerceList[];
    brandSchemes?: IBrandSchemeEntity[];
}

/**
 * Configuration for the order history rows.
 */
export interface IProductListRowProps extends IComponentProps<IProductListRowData> {
    context: ICoreContext<IAppSettingsInContext>;
    className: string;
    cartQueue: PQueue;
    actionsAvailable?: IActionsAvailable;
    resources: IProductListResources;
    productComparisonButton?: {} | null | undefined;
    moduleId: string;
    moduleType: string;
    error?: IProductError;
    allowMultiSelect?: boolean;
    allowZeroQuantity?: boolean;
    isDragOver?: 'over' | 'under' | undefined;
    isSelected?: boolean;
    readOnly?: boolean;
    working?: boolean;
    disableBuyButton?: boolean;
    disableReorder?: boolean;
    disableQuantityInput?: boolean;
    draggable?: boolean;
    historicalPrice?: boolean;
    imageSettings?: IImageSettings;
    showAvailability?: boolean;
    showPrices?: boolean;
    showTotalPrices?: boolean;
    showCartCount?: boolean;
    userHasAccess?: boolean;
    hideUnitConversions?: boolean;
    initialBreakoffEnabled?: boolean;
    smallerImage?: boolean;
    smallestImage?: boolean;
    relationTypesByIds?: IProductRelationTypeForProduct[];
    alternativeProductsMode?: 'edit' | 'view';
    retrieveOrderTemplates?(): Promise<OrderTemplate[]>;
    onProductRemove?: (product: ProductListProduct) => Promise<void>;
    onUpdateOrderTemplates?: (product?: ProductListProduct, orderTemplate?: ProductList) => Promise<OrderTemplate[]>;
    onUpdateQuantity?: (quantity: number, applyImmediately: boolean) => void;
    onBreakoffChange?: (product: ProductListProduct, enabled: boolean) => void;
    onDrop?: (event: React.DragEvent<HTMLLIElement>, recordId: number | undefined, overUnder: 'over' | 'under' | undefined) => void;
    onDragOver?: (recordId: number | undefined, overUnder: 'over' | 'under' | undefined) => void;
    onBuyValidate?: (product: ProductListProduct, quantity: number) => Promise<BuyValidationResult>;
    onChangeProduct?: OnChangeProductFunction;
    onSelectChange?: (product: ProductListProduct, isSelected: boolean, shiftKey: boolean) => void;
    onSelectionPreviewHover?: (productId: number | undefined) => void;
    selectionPreview?: 'select' | 'deselect';
    productIndex?: number;
}

export interface IProductListRowState {
    orderQuantity: number;
    working: boolean;
    imageHovered: boolean;
    showUnitConversionDetails: boolean;
    revealAlternativeProductsInfo: boolean;
}

const dragStateEventName = 'dlvry-product-drag-state';
const transparentDragImageSrc = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';
const highPriorityProductListRowCount = 6;

function randomWidth(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

function getAdditionalInformation(product: ProductListProduct): { className?: string; text?: string } {
    const className = (product.AlcoholProduct && product.ImporterGrouping) && 'importer-grouping'
        || product.Brand && 'brand'
        || product.Manufacturer && 'manufacturer'
        || product.Producer && 'producer';
    const text = (product.AlcoholProduct && product.ImporterGrouping)
        || product.Brand
        || product.Manufacturer
        || product.Producer;

    return { className: className || undefined, text: text || undefined };
}

function getAgreementDisplayState(
    product: ProductListProduct,
    customerAgreements?: IProductAgreement[]
): { icons: IProductAgreement[]; hasAgreementWithoutIcon: boolean } {
    const productAgreements = customerAgreements?.filter(agreement =>
        product.Agreements?.find(productAgreement => productAgreement.AttributeRecordId === agreement.AttributeRecordId)
    ) || [];
    const icons = productAgreements
        .filter(agreement => agreement.IconFileName && (agreement.DisplayType === 'both' || agreement.DisplayType === 'icon'))
        .reduce((acc, agreement) => {
            if (!acc.find(item => item.IconFileName === agreement.IconFileName)) {
                acc.push(agreement);
            }

            return acc;
        }, [] as IProductAgreement[]);
    const hasAgreementWithoutIcon = productAgreements.some(
        agreement => !agreement.IconFileName && (agreement.DisplayType ? agreement.DisplayType === 'both' || agreement.DisplayType === 'icon' : true)
    );

    return { icons, hasAgreementWithoutIcon };
}

/**
 * Renders rows for desktop view.
 * @param props - Configuration for the rows.
 * @returns A react node with a list of order history rows for desktop view.
 */
export class ProductListRow extends React.Component<IProductListRowProps, IProductListRowState> {

    private readonly locale = getLocale(this.props.context);

    private readonly onImageHover: DebouncedFunc<() => void>;
    private readonly onImageUnhover: DebouncedFunc<() => void>;
    private cartReactionDisposer?: IReactionDisposer;

    private _unitConversionShimmerWidth = randomWidth(42, 120);

    public constructor(props: IProductListRowProps) {
        super(props);
        this.state = {
            orderQuantity: props.data.product.CurrentQuantity ?? 0,
            working: false,
            imageHovered: false,
            showUnitConversionDetails: false,
            revealAlternativeProductsInfo: true
        };

        this.onImageHover = debounce(() => this.setState({ imageHovered: true }), 400);
        this.onImageUnhover = debounce(() => this.setState({ imageHovered: false }), 800);
    }

    public componentDidMount(): void {
        this.cartReactionDisposer = reaction(
            () => this.props.data.cart?.cart,
            () => {
                // Force update to fix cart count lagging behind
                this.forceUpdate();
            }
        );
    }

    public componentWillUnmount(): void {
        this.cartReactionDisposer?.();
        this.onImageHover.cancel();
        this.onImageUnhover.cancel();
    }

    private renderProductComparisonButton(
        productComparisonButton: React.ReactNode,
        product: any,
        catalogId: number
    ): JSX.Element | undefined {
        return React.cloneElement(productComparisonButton as React.ReactElement, { product, catalogId });
    }

    public componentDidUpdate(prevProps: Readonly<IProductListRowProps>): void {
        if (this.props.data.product.CurrentQuantity !== prevProps.data.product.CurrentQuantity) {
            this.setState({
                orderQuantity: this.props.data.product.CurrentQuantity ?? this.state.orderQuantity
            });
        }
    }

    private _renderSpecialBadges = (): React.ReactNode => {
        const { product, brandSchemes } = this.props.data;

        if (!product.SpecialBadges) {
            return null;
        }

        return product.SpecialBadges.split('|').map((badge: string) => {
            const brandScheme = brandSchemes?.find(x => x.BrandSchemeId === badge);

            return (
                <img
                    key={`${product.RecordId}-${brandScheme?.BrandSchemeId}`}
                    src={brandScheme?.ImageUrl}
                    alt={brandScheme?.BrandSchemeName}
                    title={brandScheme?.BrandSchemeName} />
            );
        });
    };

    private _renderProductName = (
        className: string,
        productUrl: string | undefined,
        agreementDisplay: { icons: IProductAgreement[]; hasAgreementWithoutIcon: boolean },
        temperatureZoneIcon?: string
    ): React.ReactNode => {
        const { context, data: { product } } = this.props;

        return (
            <a className={`${className}__product__name`} href={productUrl}>
                {product.Name && this.props.error && (
                    <span>
                        <i
                            className={`${className}__product__${this.props.error.Type}`}
                            title={this.props.error.Error} />
                        <span className='sr-only'>{this.props.error.Error}</span>
                    </span>
                )}
                {product.Transit && (
                    <div className={`${className}__product__transit-mark`} title={this.props.resources.transitTooltip || 'Transittvare'}>T</div>
                )}
                {agreementDisplay.hasAgreementWithoutIcon && (
                    <div className={`${className}__product__agreement-product-svg`} title={this.props.resources.agreementProductTooltip || 'Avtalevare'}>
                        <AgreementMark height={22} width={22} />
                    </div>
                )}
                {agreementDisplay.icons.map((icon, index) => (
                    <img
                        key={index}
                        className={`${className}__product__agreement-product-icon`}
                        src={getAsset(`agreement-icons/${icon.IconFileName}`, context.request)}
                        title={icon.IconTooltipText || icon.TooltipText}
                    />
                ))}
                {temperatureZoneIcon && (
                    <span
                        className={`${className}__product__temperature-icon ${temperatureZoneIcon}`}
                        title={product.TemperatureZone} />
                )}
                <span>{product.Name}</span>
            </a>
        );
    };

    private _renderBadgeAndCartArea = (
        className: string,
        context: ICoreContext<IAppSettingsInContext>,
        simpleProduct: SimpleProduct | undefined,
        unitConversions: UnitOfMeasureConversion[] | undefined
    ): React.ReactNode => (
        <div className={`${className}__badge-wrapper`}>
            {this._renderOrderTemplateIndicator(className)}
            {this.props.showCartCount && (
                <CartCount
                    context={context}
                    cart={this.props.data.cart?.cart}
                    product={simpleProduct}
                    nonBreakoffUOM={getNonBreakoffUOM(simpleProduct, unitConversions)}
                    inCartTooltip={this.props.resources.inCartTooltipText}
                />
            )}
        </div>
    );

    public render(): JSX.Element | undefined {
        const {
            className,
            context,
            data: { product, simpleProduct, customerAgreements, unitConversions },
            actionsAvailable,
            draggable,
            hideUnitConversions,
            initialBreakoffEnabled,
            isDragOver,
            readOnly,
            resources,
            showPrices
        } = this.props;
        const productUrl = product ? getProductPageUrlWithCategoryPathSync(context.actionContext, product, product.CategoryPaths) : '';
        const isAuthenticated = context.request.user.isAuthenticated;
        const price = showPrices !== false
            ? (product.Price && product.Price !== 0.00)
                ? product.Price : undefined
            : undefined;

        // SimpleProduct will have the current sales UOM even if product is outdated
        const unitOfMeasure = simpleProduct?.DefaultUnitOfMeasure || product.DefaultUnitOfMeasure;
        // Check if we should kg price for this product based on category settings
        const hideConversionInCategories = getHideConversionInCategories(context);
        const dontShowKgPrice = isProductInCategory(product, hideConversionInCategories);

        const conversions = unitConversions?.filter(x => x.FromUnitOfMeasureSymbol === unitOfMeasure);
        const kilogramConversion = !dontShowKgPrice && conversions && getKilogramConversion(unitOfMeasure, conversions);
        const pcsConversion = conversions && getPcsConversion(conversions, unitOfMeasure);
        const temperatureZoneIcon = product.TemperatureZone ? getTemperatureZoneIcon(context, product.TemperatureZone) : undefined;
        const additionalInformation = getAdditionalInformation(product);
        const conversionText = getConversionText(
            context,
            unitOfMeasure,
            unitConversions || [],
            simpleProduct,
            !!initialBreakoffEnabled,
            product
        );
        const unitConversionDetailsEnabled = (context.app as any).config?.enableUnitConversionDetails;
        const relationTypesForProduct = this.props.relationTypesByIds?.filter(x => x.ProductId === product.RecordId) || [];
        const showAlternativeProductsType = getShowAlternativeProductsType(context, relationTypesForProduct, product.Availability);
        const lifeCycleStatus = simpleProduct?.ExtensionProperties?.find(x => x.Key === 'PRODUCTLIFECYCLESTATEID')?.Value?.StringValue;
        const isOutOfSeason = lifeCycleStatus === LifecycleStatus.OutOfSeason;
        const allowBuying = isAvailableToBuy(context, simpleProduct, showAlternativeProductsType, product.Availability);
        const agreementBadge = getAgreementBadgeForProduct(product, customerAgreements);
        const agreementDisplay = getAgreementDisplayState(product, customerAgreements);

        return (
            <li
                key={`${product.RecordId}-${product.DefaultUnitOfMeasure}`}
                data-product-id={product.RecordId}
                className={classnames({'drag-over': isDragOver, [isDragOver || '']: isDragOver })}
                onDrop={this.handleOnDrop}
                onDragOver={this.handleDragOver}
            >
                {this._renderDragArea()}
                {this._renderSelector()}
                <div className={`${className}__product-image`}>
                    <div
                        className={classnames(`${className}__image-action-container`, {
                            small: this.props.smallerImage,
                            smallest: this.props.smallestImage
                        })}
                    >
                        <a
                            className={`${className}__image-action-container__product-image`}
                            href={productUrl}
                            onMouseEnter={() => {
                                this.onImageUnhover.cancel();
                                this.onImageHover();
                            }}
                            onMouseLeave={() => {
                                this.onImageHover.cancel();
                                this.onImageUnhover();
                            }}
                            style={{ zIndex: /* this.state.imageHovered ? 9998 : */ undefined }}
                        >
                            {this._renderProductImage(
                                'product-image',
                                context,
                                product.Name || '',
                                product.PrimaryImageUrl,
                                this.props.imageSettings
                            )}
                            {(product.Badge || agreementBadge) && (
                                <div className={`${className}__image-badge-wrapper`}>
                                    {product.Badge && (
                                        <DiscountBadge
                                            context={context}
                                            badge={product.Badge}
                                            size='medium'
                                            sizeMultiplier={2} />
                                    )}

                                    {agreementBadge?.Badge && (
                                        <DiscountBadge
                                            context={context}
                                            badge={agreementBadge.Badge}
                                            size='medium'
                                            title={agreementBadge.TooltipText}
                                            sizeMultiplier={2} />
                                    )}
                                </div>
                            )}
                        </a>
                        {this._renderProductImageActions()}
                    </div>

                    {product.SpecialBadges && (
                        <div className={`${className}__special-badges`} style={{ zIndex: /* this.state.imageHovered ? 9999 : */ undefined }}>
                            {this._renderSpecialBadges()}
                        </div>
                    )}
                </div>

                <div className={`${className}__product-wrapper`}>
                    <div className={`${className}__product`}>
                        {product.Category && (
                            <div className={`${className}__product__category`}>
                                {product.Category}
                            </div>
                        )}
                        <div className={`${className}__product__name-wrapper`}>
                            {this._renderProductName(className, productUrl, agreementDisplay, temperatureZoneIcon)}
                        </div>
                        <div className={`${className}__product__information-wrapper`}>
                            <div className={`${className}__product__id`}>
                                {product.ItemId}
                            </div>
                            {(product.Producer && product.Producer !== additionalInformation.text) && (
                                <div className={`${className}__product__producer`}>
                                    {product.Producer}
                                </div>
                            )}
                            {additionalInformation.className && (
                                <div className={`${className}__product__${additionalInformation.className}`}>
                                    {additionalInformation.text}
                                </div>
                            )}
                            {!hideUnitConversions && isAuthenticated && (unitConversions === undefined || !simpleProduct || conversionText) && (
                                <div
                                    className={classnames(`${className}__product__unit-conversion`, {
                                        enabled: unitConversionDetailsEnabled,
                                        retrieving: unitConversions === undefined || !simpleProduct
                                    })}
                                    style={{ minWidth: unitConversions === undefined || !simpleProduct ? this._unitConversionShimmerWidth : undefined }}
                                    onClick={() => this.setState({ showUnitConversionDetails: true })}
                                >
                                    {conversionText}
                                    <UnitConversionDetails
                                        context={context}
                                        enabled={unitConversionDetailsEnabled}
                                        isOpen={this.state.showUnitConversionDetails}
                                        toggle={() => this.setState({ showUnitConversionDetails: !this.state.showUnitConversionDetails })}
                                        productName={product.Name}
                                        productUOM={unitOfMeasure}
                                        unitConversions={unitConversions || []}
                                        simpleProduct={simpleProduct}
                                        breakoff={initialBreakoffEnabled}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {!isAuthenticated && (
                    <div className={`${className}__price__not-logged-in`}>
                        {resources.priceNotLoggedInText || 'Logg inn for å se priser'}
                    </div>
                )}

                {isAuthenticated && (
                    <div className={classnames(`${className}__right`, { 'read-only': readOnly })}>
                        <div className={classnames(`${className}__price-and-indicators`, { 'add-margin': actionsAvailable?.removeAction })}>
                            {allowBuying && (
                                <div className={`${className}__prices-wrapper`}>
                                    <div className={`${className}__price`}>
                                        {this._renderPrice(className, price)}
                                    </div>

                                    {price && this.props.historicalPrice && (
                                        <div className={`${className}__price__extra`}>
                                            <PriceComponent
                                                context={context}
                                                data={{ price: { CustomerContextualPrice: price / this.state.orderQuantity } }}
                                                id={this.props.id}
                                                typeName={this.props.typeName} />
                                            &nbsp;
                                            pr. {humanizeUnitOfMeasure(context, unitOfMeasure).toLowerCase()}
                                        </div>
                                    )}

                                    {price && !this.props.historicalPrice && !!(!initialBreakoffEnabled && simpleProduct?.Behavior?.MultipleOfQuantity && simpleProduct.Behavior.MultipleOfQuantity > 1) && (
                                        <div className={`${className}__price__extra`}>
                                            <PriceComponent
                                                context={context}
                                                data={{ price: { CustomerContextualPrice: price } }}
                                                id={this.props.id}
                                                typeName={this.props.typeName} />
                                            &nbsp;
                                            pr. {humanizeUnitOfMeasure(context, unitOfMeasure).toLowerCase()}
                                        </div>
                                    )}

                                    {price && pcsConversion && (
                                        <div className={`${className}__price__extra`}>
                                            <PriceComponent
                                                    context={context}
                                                    data={{ price: { CustomerContextualPrice: price / (pcsConversion.Factor || 1) } }}
                                                    id={this.props.id}
                                                    typeName={this.props.typeName} />
                                            &nbsp;
                                            pr. stk
                                        </div>
                                    )}

                                    {price && kilogramConversion && getRoundedFactor(kilogramConversion) > 0 && (
                                        <div className={`${className}__price__extra`}>
                                            <PriceComponent
                                                context={context}
                                                data={{ price: { CustomerContextualPrice: price / getRoundedFactor(kilogramConversion) } }}
                                                id={this.props.id}
                                                typeName={this.props.typeName} />
                                            &nbsp;
                                            pr. kg
                                        </div>
                                    )}

                                    {price && conversions && getLiterPrice(conversions, unitOfMeasure, price) && (
                                        <div className={`${className}__price__extra`}>
                                            <PriceComponent
                                                context={context}
                                                data={{ price: { CustomerContextualPrice: getLiterPrice(conversions, unitOfMeasure, price) } }}
                                                id={this.props.id}
                                                typeName={this.props.typeName} />
                                            &nbsp;
                                            pr. liter
                                        </div>
                                    )}

                                    {this._renderDepositPrice(price)}
                                </div>
                            )}

                            {this._renderBadgeAndCartArea(className, context, simpleProduct, unitConversions)}
                        </div>

                        {allowBuying && this.props.readOnly && (
                            <div className={`${className}__quantity-wrapper`}>
                                <span style={{ fontSize: 14 }}>
                                    {/* We use product.DefaultUnitOfMeasure here because of historical data I think */}
                                    {this.state.orderQuantity} {humanizeUnitOfMeasure(context, product.DefaultUnitOfMeasure, simpleProduct).toLocaleLowerCase()}
                                </span>
                            </div>
                        )}

                        {allowBuying && !this.props.readOnly && (
                            <div className={`${className}__quantity-wrapper`}>
                                <QuantityInput
                                    context={context}
                                    disable={this.props.disableQuantityInput}
                                    mode={readOnly ? 'view' : 'edit'}
                                    product={simpleProduct}
                                    extProduct={product}
                                    quantity={this.state.orderQuantity}
                                    breakoff={!!initialBreakoffEnabled}
                                    onQuantityChange={this._onUpdateQuantity}
                                    onBreakoffChange={(enabled) => this.props.onBreakoffChange?.(product, enabled)}
                                    nonBreakoffUOM={getNonBreakoffUOM(simpleProduct, unitConversions)}
                                    allowZero={this.props.allowZeroQuantity}
                                    unitConversions={unitConversions}
                                />
                            </div>
                        )}

                        {this.props.showTotalPrices && allowBuying &&
                            this._renderTotalPrice(className, price)
                        }

                        {!allowBuying && isOutOfSeason && (
                            <div className={`${className}__out-of-season-indicator`}>
                                Varen er ute av sesong
                            </div>
                        )}
                    </div>
                )}

                {isAuthenticated && (
                    <div className={`${className}__bottom`}>
                        <div className={`${className}__bottom-left`}>
                            {this._renderAvailability(allowBuying)}
                        </div>

                        {actionsAvailable && (
                            <div className={`${className}__actions`}>
                                {showAlternativeProductsType && (
                                    <>
                                        {!isOutOfSeason && showAlternativeProductsType === 'Primary' && product.Availability?.StockLevelCode === 'OOS' && (
                                            <div className={classnames(`${className}__change-product__information__wrapper`)}>
                                                <div className={`${className}__change-product__information__text`}>
                                                    Varen er sluttsolgt
                                                </div>
                                            </div>
                                        )}
                                        <LocalDropdown
                                            trigger={({ ref, onClick, 'aria-expanded': ariaExpanded }) => (
                                                <button
                                                    ref={ref as any}
                                                    className={classnames(`${className}__change-product__button`, { primary: showAlternativeProductsType === 'Primary' })}
                                                    onClick={() => {
                                                        void GTM.beginAltProductReplaceedAsync(product);
                                                        onClick();
                                                    }}
                                                    aria-expanded={ariaExpanded}
                                                >
                                                    {resources.alternativeProductsButtonText}
                                                </button>
                                            )}
                                        >
                                            {(close) => (
                                                <AlternativeProducts
                                                    id={this.props.id}
                                                    typeName={this.props.typeName}
                                                    context={context}
                                                    mode={this.props.alternativeProductsMode || 'view'}
                                                    resources={{
                                                        alternativeProductsChangeProductButtonText: resources.alternativeProductsChangeProductButtonText,
                                                        alternativeProductsLoadingText: resources.alternativeProductsLoadingText,
                                                        alternativeProductsNoResultsText: resources.alternativeProductsNoResultsText,
                                                        alternativeProductsErrorText: resources.alternativeProductsErrorText,
                                                        alternativeProductsCloseButtonText: resources.alternativeProductsCloseButtonText
                                                    }}
                                                    product={product}
                                                    relationTypesByIds={this.props.relationTypesByIds}
                                                    onChangeProduct={this.props.onChangeProduct}
                                                    close={close}
                                                />
                                            )}
                                        </LocalDropdown>
                                    </>
                                )}
                                {actionsAvailable?.addToCartAction && (
                                    this._getCartButton(allowBuying)
                                )}
                                {actionsAvailable?.addToCartOrderTemplate && (
                                    this._getOrderTemplateBuyButton(allowBuying)
                                )}
                                {actionsAvailable?.productComparisonButton && this.props.productComparisonButton && this.renderProductComparisonButton(
                                    this.props.productComparisonButton,
                                    product,
                                    getCatalogId(context.request))}
                            </div>
                        )}

                        {((actionsAvailable && Object.values(actionsAvailable).some(Boolean)) || draggable) && (
                            <div className={`${className}__line-actions`}>
                                {actionsAvailable?.removeAction && this._getRemoveActionButton(
                                    'btn-remove',
                                    resources.removeButtonText || 'Fjern',
                                    this._removeItem)}
                            </div>
                        )}
                    </div>
                )}
            </li>
        );
    }

    private readonly handleOnDrop = (e: React.DragEvent<HTMLLIElement>) => {
        e.stopPropagation(); // Prevent onDrop handler on parent from firing

        const { data: { product }, isDragOver, onDrop } = this.props;
        onDrop?.(e, product.RecordId, isDragOver);
    };

    private readonly handleDragOver = (e: React.DragEvent<HTMLLIElement>) => {
        e.stopPropagation();

        const { data: { product }, disableReorder, onDragOver } = this.props;

        if (!disableReorder && e.dataTransfer.types.includes('product')) {
            e.preventDefault();

            const rect = (e.currentTarget as Element).getBoundingClientRect();
            const y = e.clientY - rect.top;
            const halfHeight = rect.height / 2;
            const overOrUnder: 'over' | 'under' = y > halfHeight ? 'under' : 'over';

            onDragOver?.(product.RecordId, overOrUnder);
        }
    };

    private readonly _handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
        e.stopPropagation();

        const { product } = this.props.data;
        const transparentDragImage = document.createElement('img');

        transparentDragImage.src = transparentDragImageSrc;

        e.dataTransfer.setData('product', JSON.stringify({
            ProductName: product.Name || '',
            ProductRecordId: product.RecordId || 0,
            ProductUnitOfMeasure: product.DefaultUnitOfMeasure || ''
        }));
        e.dataTransfer.setDragImage(transparentDragImage, 0, 0);
        e.dataTransfer.effectAllowed = 'copyMove';

        window.dispatchEvent(new CustomEvent(dragStateEventName, {
            detail: {
                active: true,
                productId: product.RecordId,
                productName: product.Name || '',
                x: e.clientX,
                y: e.clientY
            }
        }));
    };

    private readonly _renderDragArea = () => {
        const { context, draggable, onDragOver } = this.props;

        if (!draggable) return null;

        return (
            <div
                className='drag-area'
                draggable
                onDragStart={this._handleDragStart}
                onDragEnd={() => {
                    onDragOver?.(undefined, undefined);
                    window.dispatchEvent(new CustomEvent(dragStateEventName, {
                        detail: {
                            active: false
                        }
                    }));
                }}
                onDragOver={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                }}
            >
                <img className='drag-area__icon' src={getAsset('icons/drag-handle.svg', context.request)} />
            </div>
        );
    };

    private readonly _renderSelector = () => {
        const {
            context,
            data: { product },
            allowMultiSelect,
            isSelected,
            selectionPreview,
            onSelectChange,
            onSelectionPreviewHover
        } = this.props;
        const { isAuthenticated } = context.request.user;

        if (!isAuthenticated || !allowMultiSelect) return null;

        return (
            <label
                className={classnames('checkbox-container', {
                    [`selection-preview--${selectionPreview}`]: selectionPreview
                })}
                onMouseEnter={() => onSelectionPreviewHover?.(product.RecordId)}
                onMouseLeave={() => onSelectionPreviewHover?.(undefined)}
            >
                <input
                    aria-label='selectOneRowText'
                    className='checkbox-input'
                    type='checkbox'
                    checked={!!isSelected}
                    aria-checked={!!isSelected}
                    onChange={(e) => {
                        onSelectChange?.(product, e.target.checked, (e.nativeEvent as MouseEvent).shiftKey);
                    }}
                />
                <span className='checkmark'/>
            </label>
        );
    };

    private _renderPrice = (className: string, price: number | undefined): React.ReactNode => {
        const {
            context,
            id,
            typeName,
            data: { product, simpleProduct },
            resources,
            initialBreakoffEnabled,
            historicalPrice
        } = this.props;

        if (!context.request.user.isAuthenticated) {
            return undefined;
        }

        if (product.PriceFailed) {
            return <span className={`${className}__price__error`}>{resources.priceErrorText || 'Pris n/a'}</span>;
        }

        if (!price || !simpleProduct) {
            return <span className={`${className}__price__retrieving`}>-</span>;
        }

        let productPrice: ProductPrice | undefined;

        if (historicalPrice) {
            // If price is historical we only wanna show the price as is
            productPrice = {
                CustomerContextualPrice: price
            };
        } else {
            // If not use latest price if available otherwise whatever is set on the product
            productPrice = product.ProductPrice ? product.ProductPrice : {
                CustomerContextualPrice: price,
                BasePrice: product.BasePrice
            };
        }

        if (!historicalPrice && !initialBreakoffEnabled && simpleProduct.Behavior?.MultipleOfQuantity) {
            productPrice = multiplyPriceBy(productPrice, simpleProduct.Behavior.MultipleOfQuantity);
        }

        return (
            <PriceComponent
                context={context}
                data={{ price: productPrice }}
                id={id}
                typeName={typeName}
                discountPercentageSide='right' />
        );
    };

    private _renderTotalPrice = (className: string, price: number | undefined): React.ReactNode => {
        const totalPrice: ProductPrice = {
            CustomerContextualPrice: (price || 0) * this.state.orderQuantity
        };

        const hasMixAndMatchDiscount = this.props.data.product.ProductPrice?.DiscountLines?.some(discount => discount.PeriodicDiscountTypeValue === PeriodicDiscountOfferType.MixAndMatch);

        if (hasMixAndMatchDiscount && this.props.data.product.ProductPrice && 'NetPrice' in this.props.data.product.ProductPrice) {
            totalPrice.CustomerContextualPrice = (this.props.data.product.ProductPrice as any).TotalAmount as number;
            totalPrice.BasePrice = (this.props.data.product.ProductPrice as any).NetPrice as number;
            totalPrice.DiscountLines = (this.props.data.product.ProductPrice as any).DiscountLines;
        }

        const multiplier = this.props.initialBreakoffEnabled ? 1 : this.props.data.simpleProduct?.Behavior?.MultipleOfQuantity || 1;
        const quantityIsNotDefault = this.state.orderQuantity !== 0 && this.state.orderQuantity !== multiplier;

        return (
            <div className={classnames(`${className}__total-price-wrapper`, { changed: quantityIsNotDefault, retrieving: price === undefined })}>
                <div className={`${className}__total-price__label`} data-failed={this.props.data.product.PriceFailed}>
                    Totalt:
                </div>
                <div className={`${className}__total-price`} data-failed={this.props.data.product.PriceFailed}>
                    <PriceComponent
                        context={this.props.context}
                        data={{ price: totalPrice }}
                        id={this.props.id}
                        freePriceText={price === undefined ? ' ' : undefined}
                        typeName={this.props.typeName} />
                    {hasMixAndMatchDiscount && (
                        <div>
                            <span className={`${className}__total-price__mix-and-match-discount`}>
                                {/* this.props.resources.mixAndMatchDiscountText || */ 'Mix & Match'}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    private _renderDepositPrice = (price: number | undefined): React.ReactNode => {
        const {
            context,
            className,
            id,
            typeName,
            data: { simpleProduct },
            resources
        } = this.props;

        if (!context.app.config?.enableDepositPriceDisplay) {
            return null;
        }

        const deposit = simpleProduct?.ExtensionProperties?.find(x => x.Key === 'DEPOSIT')?.Value?.DecimalValue;

        if (!price || !deposit) {
            return null;
        }

        return (
            <div className={`${className}__price__extra`}>
                +
                &nbsp;
                <PriceComponent
                    context={context}
                    data={{ price: { CustomerContextualPrice: deposit } }}
                    id={id}
                    typeName={typeName} />
                &nbsp;
                {resources.depositSuffixText || 'pant'}
            </div>
        );
    };

    private _renderOrderTemplateIndicator = (baseClassName: string): JSX.Element | undefined => {
        let orderTemplateIndicator: JSX.Element | undefined;
        const { context, data: { product } } = this.props;

        if (!context.request.user.isAuthenticated) {
            return undefined;
        }

        const orderTemplateIndicatorRef = React.createRef<HTMLDivElement>();

        if (product.PartOfProductLists && product.PartOfProductLists.length > 0) {
            orderTemplateIndicator = (
                <div style={{ display: 'inline-block' }}>
                    <div
                        ref={orderTemplateIndicatorRef}
                        className={`${baseClassName}__badge order-template-indicator`}
                    >
                        P
                    </div>
                    <UncontrolledTooltip
                        className={`${baseClassName}__order-template-indicator__tooltip`}
                        placement='bottom-end'
                        trigger='hover focus'
                        delay={{ show: 200, hide: 200 }}
                        target={orderTemplateIndicatorRef}
                    >
                        <div className={`${baseClassName}__order-template-indicator__tooltip-header`}>
                            {/* inOrderTemplateTooltipHeader ||  */'Finnes i plankjøp:'}
                        </div>
                        {product.PartOfProductLists && product.PartOfProductLists.length > 0 && (
                            <ul>
                                {product.PartOfProductLists.map((productList: IProductListByProductId) => {
                                    let url = getUrlSync('ordertemplate', context.actionContext) ?? '';
                                    const separator = url.includes('?') ? '&' : '?';

                                    if (productList.ProductListId) {
                                        url = `${url}${separator}id=${productList.ProductListId}`;
                                    }

                                    return (
                                        <li key={productList.ProductListId}>
                                            <a href={url}>{productList.Name}</a>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </UncontrolledTooltip>
                </div>
            );
        }

        return orderTemplateIndicator;
    };

    private _getAvailabilityClassName = (): 'hidden' | 'retrieving' | 'available' | 'error' | 'not-available' => {
        const { product, simpleProduct } = this.props.data;
        const { showAvailability } = this.props;

        if (!showAvailability) {
            return 'hidden';
        }

        if (!product.Availability || !simpleProduct) {
            return 'retrieving';
        }

        if (product.Availability.StockLevelLabel?.startsWith('!')) {
            return 'available';
        }

        if (product.Availability.IsProductAvailable) {
            return 'available';
        }

        if (product.Availability.StockLevelCode === 'ERR') {
            return 'error';
        }

        return 'not-available';
    };

    private _renderAvailability = (allowBuying: boolean): React.ReactNode => {
        const {
            className,
            data: { product },
            showAvailability
        } = this.props;

        if (!showAvailability || !allowBuying) {
            return null;
        }

        const availabilityClass = this._getAvailabilityClassName();
        const stockLevelLabel = product.Availability?.StockLevelLabel?.startsWith('!')
            ? product.Availability?.StockLevelLabel?.substring(1)
            : product.Availability?.StockLevelLabel;
        const availabilityText = stockLevelLabel || (availabilityClass === 'error' ? 'ERROR' : '');

        return (
            <div className={`${className}__product__availability__wrapper`}>
                <ExpandableEllipsis
                    className='ellipsisCompact'
                    overlayClassName='product-list-component__availability-ellipsis-overlay'
                    minWidth={200}
                >
                    <div className={classnames(`${className}__product__availability`, availabilityClass)}>
                        {/* <img className={`${className}__product__availability__icon`} src={availabilityIcon} /> */}
                        <div className={`${className}__product__availability__indicator`} />
                        <span className='text-overflow-target'>
                            &nbsp;
                            {availabilityText}
                        </span>
                    </div>
                </ExpandableEllipsis>
            </div>
        );
    };

    private _getOrderTemplateBuyButton = (allowBuying: boolean): React.ReactNode => {
        const working = this.props.working || this.state.working;
        const disabled = !allowBuying || this.props.disableBuyButton || working || this.state.orderQuantity <= 0;
        let text = this.locale.cartButton.buy;

        if (!allowBuying) {
            const product = this.props.data.simpleProduct;
            const lifeCycleStatus = product?.ExtensionProperties?.find(x => x.Key === 'PRODUCTLIFECYCLESTATEID')?.Value?.StringValue;
            text = lifeCycleStatus === LifecycleStatus.OutOfSeason ? this.locale.cartButton.outOfSeason : this.locale.cartButton.expired;
        }

        return (
            <Button
                className={classnames('btn-cart-button', { loading: working })}
                title={text}
                aria-label={text}
                disabled={disabled}
                onClick={this._addItemToCartAlways}
            >
                {text}
            </Button>
        );
    };

    private _getCartButton = (allowBuying: boolean): React.ReactNode => {
        const { data: { simpleProduct }, cartQueue, disableBuyButton } = this.props;
        const { working } = this.state;

        const baseProps: ButtonProps = {
            className: classnames('btn-cart-button', { loading: working }),
            disabled: disableBuyButton || working
        };

        if (!allowBuying) {
            const lifeCycleStatus = simpleProduct?.ExtensionProperties?.find(x => x.Key === 'PRODUCTLIFECYCLESTATEID')?.Value?.StringValue;
            const text = lifeCycleStatus === LifecycleStatus.OutOfSeason ? this.locale.cartButton.outOfSeason : this.locale.cartButton.expired;
            return (
                <Button {...baseProps} aria-label={text} title={text} disabled={true}>
                    {text}
                </Button>
            );
        }

        const clickHandler = async () => {
            this.setState({ working: true });
            await cartQueue.add(async () => {
                await this._addItemToCartAlways();
            });
            this.setState({ working: false });
        };

        const text = this.locale.cartButton.buy;
        return (
            <Button {...baseProps} aria-label={text} onClick={clickHandler} title={text}>
                {text}
            </Button>
        );
    };

    private readonly _onUpdateQuantity = (newQuantity: number, applyImmediately: boolean = false) => {
        const { onUpdateQuantity } = this.props;

        this.setState({ orderQuantity: newQuantity });

        onUpdateQuantity?.(newQuantity, applyImmediately);
    };

    private readonly _renderProductImage = (
        className: string,
        context: ICoreContext,
        heading: string,
        image?: string,
        imageSettings?: IImageSettings
    ): JSX.Element | null => {
        const width = 580;
        const height = 400;
        const defaultImageSettings: IImageSettings = {
            viewports: {
                xs: { w: width, h: height, q: `w=${width}&h=${height}&q=80&m=0&f=webp`, qe: `t=${width}h${height}qhigh` },
                lg: { w: width, h: height, q: `w=${width}&h=${height}&q=80&m=0&f=webp`, qe: `t=${width}h${height}qhigh` },
                xl: { w: width, h: height, q: `w=${width}&h=${height}&q=80&m=0&f=webp`, qe: `t=${width}h${height}qhigh` }
            },
            disableLazyLoad: Boolean(imageSettings?.disableLazyLoad),
            lazyload: Boolean(imageSettings?.lazyload),
            quality: imageSettings?.quality || 80
        };

        const hasRecordId = !!this.props.data.product.RecordId;

        image = ensureBaseImageUrl(context, image);

        const contextConfig = context.request.app.config?.placeholderImageName;
        const emptyPlaceHolderImage = contextConfig as string;
        let fallbackImageSource = context?.request.apiSettings.baseImageUrl;
        if (fallbackImageSource?.endsWith('/')) {
            fallbackImageSource = fallbackImageSource.slice(0, -1);
        }
        if (emptyPlaceHolderImage) {
            fallbackImageSource = `${fallbackImageSource}${emptyPlaceHolderImage}`;
        }

        return (
            <Image
                requestContext={context.actionContext.requestContext}
                className={className}
                altText={heading}
                src={hasRecordId ? (image || fallbackImageSource) : undefined}
                fallBackSrc={hasRecordId ? fallbackImageSource : undefined}
                gridSettings={context.request.gridSettings!}
                imageSettings={defaultImageSettings}
                loadFailureBehavior='empty'
                bypassHideOnFailure
                fetchPriority={this.props.productIndex !== undefined && this.props.productIndex < highPriorityProductListRowCount ? ImageFetchPriority.High : undefined}
                tabIndex={0}
            />
        );
    };

    private readonly _renderProductImageActions = () => {
        const {
            actionsAvailable,
            className,
            context,
            data: { product, simpleProduct, ...data }
        } = this.props;
        const { isAuthenticated } = context.request.user;

        if (!isAuthenticated) return null;

        return (
            <div className={`${className}__image-action-container__actions`}>
                {this.props.retrieveOrderTemplates && (
                    <AddToOrderTemplateComponent
                        className='order-template-button'
                        addToOrderTemplateButtonText={this.locale.addToOrderTemplate.addToOrderTemplateButtonText || 'addToOrderTemplateButtonText'}
                        addToOrderTemplateButtonTooltip={this.locale.addToOrderTemplate.addToOrderTemplateButtonTooltip || 'addToOrderTemplateButtonTooltip'}
                        addToOrderTemplateDialogResources={generateAddToOrderTemplateDialogResources(this.locale)}
                        createOrderTemplateDialogResources={generateOrderTemplateNameDialogResources(this.locale)}
                        itemAddedToOrderTemplateDialogResources={generateItemAddedToOrderTemplateDialogResources(this.locale)}
                        duplicateItemsWhenAddingToOrderTemplateDialogResources={generateDuplicateItemsWhenAddingToOrderTemplateDialogResources(this.locale)}
                        context={context}
                        id={this.props.moduleId}
                        typeName={this.props.moduleType}
                        disabled={!simpleProduct || !data.customerInformation}
                        disableButton={!simpleProduct || !data.customerInformation}
                        data={{
                            orderTemplates: data.orderTemplates,
                            product: simpleProduct || {} as any,
                            quantity: this.state.orderQuantity || 1,
                            customerInformation: data.customerInformation || { IsB2b: true } as any, // Always pass 'IsB2b' so that it will always render (unless false from api)
                            partOfProductLists: product.PartOfProductLists
                        }}
                        onSuccess={async (orderTemplate) => {
                            await this.props.onUpdateOrderTemplates?.(product, orderTemplate);
                            toast({
                                text: `${product.Name}${this.locale.addToOrderTemplate.itemAddedToOrderTemplateHeaderMessageText}`,
                                autoClose: 3000,
                                canClose: true,
                                position: 'bottom-right',
                                type: 'success',
                                showProgress: true
                            });
                        }}
                        retrieveOrderTemplates={this.props.retrieveOrderTemplates}
                    />
                )}
                {actionsAvailable?.addToWishlistAction && this._getWishlistActionButton()}
            </div>
        );
    };

    private readonly _getRemoveActionButton = (className: string, buttonText: string, clickHandler: (event: React.MouseEvent<HTMLElement>) => void): React.ReactNode => {
        return (
            <Button aria-label={buttonText} className={className} onClick={clickHandler} title={buttonText}>
                <span className='fa-regular fa-xmark no-margin' />
            </Button>
        );
    };

    private readonly _getWishlistActionButton = (): React.ReactNode => {
        const { resources } = this.props;
        const { product, wishlists } = this.props.data;
        const isWishlisted = (wishlists?.length || 0) > 0 && wishlists![0].CommerceListLines?.some(x => x.ProductId === product.RecordId);
        const buttonText = isWishlisted
            ? resources.removeFromWishlistText || 'Fjern fra huskelisten'
            : resources.addToWishlistText || 'Legg til i huskelisten';
        const onClick = isWishlisted ? this.removeItemFromWishlist : this.moveItemToWishlist;

        return (
            <Button
                className={classnames('btn-add-to-wishlist', { 'wishlisted': isWishlisted })}
                title={buttonText}
                aria-label={buttonText}
                onClick={onClick} />
        );
    };

    private readonly _removeItem = async (): Promise<void> => {
        await this.props.onProductRemove?.(this.props.data.product);
    };

    private readonly moveItemToWishlist = async(): Promise<void> => {
        const { context } = this.props;
        const { product } = this.props.data;

        if (!context || !product || !context.request.user.catalogId) {
            return Promise.resolve();
        }

        if (context.request.user.isAuthenticated) {
            const customer = await this.getCustomerAccount();

            let wishlists = this.props.data.wishlists;

            if (!wishlists) {
                wishlists = await getWishlistByCustomerId(createGetByCustomerInput({}, null), context.actionContext);
            }

            if (!wishlists || wishlists.length === 0) {
                const wishlistName = 'My wishlist';
                if (customer && customer.AccountNumber) {
                    const listData: CommerceList = {
                        CustomerId: customer.AccountNumber,
                        Name: wishlistName,
                        CommerceListTypeValue: 1,
                        Id: 0
                    };
                    try {
                        const commerceList = await createCommerceListAsync({
                                callerContext: this.props.context.actionContext
                            },
                            listData);
                        await this.callAddWishlistLine(product.RecordId, commerceList.Id);
                    } catch (err) {
                        const error = err as unknown as any;
                        const footer = error?.data?.CorrelationId ? `Correlation Id: ${error?.data?.CorrelationId}` : undefined;
                        toasts.error(this.locale.toastStrings.errorHeader, error, { footer });
                    }
                }
            } else {
                await this.callAddWishlistLine(product.RecordId, wishlists[0].Id);

                toast({
                    text: `${product.Name} lagt i huskelisten`,
                    type: 'success',
                    autoClose: 3000,
                    position: 'bottom-right',
                    canClose: true,
                    showProgress: false
                });
            }
        }
    };

    private readonly removeItemFromWishlist = async(): Promise<void> => {
        const { context } = this.props;
        const { product } = this.props.data;

        if (!context || !product || !context.request.user.catalogId || !context.request.user.isAuthenticated) {
            return Promise.resolve();
        }

        let wishlists = this.props.data.wishlists;

        if (!wishlists) {
            wishlists = await getWishlistByCustomerId(createGetByCustomerInput({}, null), context.actionContext);
        }

        if (wishlists.length > 0) {
            const commerceLineToRemove = wishlists[0].CommerceListLines?.find(x => x.ProductId === product.RecordId);
            const updatedCommerceList = await removeLinesAsync({ callerContext: context.actionContext },
                wishlists[0].Id,
                [commerceLineToRemove!]);
            context.actionContext.update(createGetByCustomerInput({}, null), [updatedCommerceList]);

            toast({
                text: `${product.Name} fjernet fra huskelisten`,
                type: "success",
                autoClose: 3000,
                position: "bottom-right",
                canClose: true,
                showProgress: false
            });
        }
    };

    private readonly callAddWishlistLine = async (productId: number, wishlistId: number): Promise<void> => {
        const { context } = this.props;

        try {
            const customer = await this.getCustomerAccount();
            if (customer && customer.AccountNumber) {
                const createdCommerceList = await addLinesAsync({ callerContext: context.actionContext },
                    wishlistId,
                    [
                        buildWishlistLine(productId, customer.AccountNumber, wishlistId)
                    ]);
                context.actionContext.update(createGetByCustomerInput({}, null), [createdCommerceList]);
            }
        } catch (err) {
            const error = err as unknown as any;
            const footer = error?.data?.CorrelationId ? `Correlation Id: ${error?.data?.CorrelationId}` : undefined;
            toasts.error(this.locale.toastStrings.errorHeader, error, { footer });
        }
    };

    private readonly getCustomerAccount = (): AsyncResult<Customer> => {
        const customerInput = new GetCustomerInput(this.props.context.actionContext.requestContext.apiSettings);
        return getCustomer(customerInput, this.props.context.actionContext);
    };

    private readonly _addItemToCartAlways = async (): Promise<void> => {
        const { context } = this.props;
        const { product, simpleProduct } = this.props.data;

        if (!context || !product || !simpleProduct || !context.request.user.catalogId) {
            return Promise.resolve();
        }

        this.setState({ working: true });

        // @ts-expect-error -- Result not used at the moment
        const success = await CartHelper.addToCartAsync(
            context,
            product,
            simpleProduct,
            this.props.userHasAccess,
            this.state.orderQuantity,
            this.props.initialBreakoffEnabled,
            this.props.onBuyValidate ? async () => this.props.onBuyValidate!(product, this.state.orderQuantity) : undefined,
            getNonBreakoffUOM(simpleProduct, this.props.data.unitConversions)
        );

        this.setState({ orderQuantity: 0, working: false });
        this.props.onUpdateQuantity?.(0, true);
    };
}
